import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { Loader } from '../../components/util/Svg';
import { generateWeeklyReport, updateCredits } from '../../services/reportService';
import { getDisplayMessage } from '../../helper';

const groupByMonthAndDate = (rows) => {
    const result = {};
    (rows || []).forEach((row) => {
        if (!row.dateIST) {
            return;
        }
        const dt = new Date(row.dateIST);
        const month = dt.toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
        const day = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        if (!result[month]) {
            result[month] = {};
        }
        if (!result[month][day]) {
            result[month][day] = [];
        }
        result[month][day].push(row);
    });
    return result;
};

const WeeklyReportPanel = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startDateErr, setStartDateErr] = useState('');
    const [endDateErr, setEndDateErr] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loader, setLoader] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const validateDates = () => {
        let valid = true;
        setStartDateErr('');
        setEndDateErr('');
        setErrorMessage('');

        if (!startDate) {
            setStartDateErr('Start date is required');
            valid = false;
        }
        if (!endDate) {
            setEndDateErr('End date is required');
            valid = false;
        }
        if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'day')) {
            setEndDateErr('End date cannot be earlier than start date');
            valid = false;
        }
        return valid;
    };

    const buildParams = useCallback((filters, limit = 3000) => {
        const query = new URLSearchParams();
        query.set('page', '1');
        query.set('limit', String(limit));
        query.set('search', '');
        query.set('filterBy', '');
        if (filters) {
            query.set('startDate', filters.startDate);
            query.set('endDate', filters.endDate);
        }
        return `?${query.toString()}`;
    }, []);

    const fetchReport = useCallback(async (filters) => {
        setLoader(true);
        setErrorMessage('');
        const res = await generateWeeklyReport(buildParams(filters));
        setLoader(false);

        const rows = res.data && (res.data.userList || res.data.list);
        if (Array.isArray(rows)) {
            setReportData(rows);
            setErrorMessage('');
        } else {
            setReportData([]);
            setErrorMessage(getDisplayMessage(res.data, 'Unable to generate weekly report'));
        }
    }, [buildParams]);

    const handleGenerate = (e) => {
        e.preventDefault();
        if (!validateDates()) {
            return;
        }
        const filters = { startDate, endDate };
        setAppliedFilters(filters);
        fetchReport(filters);
    };

    const handleReset = () => {
        setStartDate('');
        setEndDate('');
        setStartDateErr('');
        setEndDateErr('');
        setErrorMessage('');
        setAppliedFilters(null);
        setReportData(null);
    };

    const handleUpdatePoints = async () => {
        setIsUpdating(true);
        setErrorMessage('');
        const res = await updateCredits();
        setIsUpdating(false);
        if (res.status) {
            if (appliedFilters) {
                fetchReport(appliedFilters);
            }
        } else {
            setErrorMessage(getDisplayMessage(res.data, 'Failed to update points'));
        }
    };

    const handleExport = async () => {
        if (!reportData || !reportData.length) {
            setErrorMessage('No records found to export');
            return;
        }

        setExporting(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Weekly Report');
            const grouped = groupByMonthAndDate(reportData);
            const allDates = Object.entries(grouped).flatMap(([, days]) => Object.keys(days));
            const headerRow = sheet.addRow(['User Name', ...allDates]);
            headerRow.font = { bold: true };

            const allUsers = Array.from(new Set(reportData.map((u) => u.userName)));
            allUsers.forEach((userName) => {
                const row = [userName];
                allDates.forEach((date) => {
                    const month = Object.keys(grouped).find((m) => grouped[m][date]);
                    const user = month ? grouped[month][date].find((u) => u.userName === userName) : null;
                    row.push(user ? `Rem: ${user.remainingPoints} / Tot: ${user.totalCredits || 0}` : '-');
                });
                sheet.addRow(row);
            });

            sheet.columns.forEach((column) => {
                let maxLength = 12;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    const value = cell.value ? String(cell.value) : '';
                    maxLength = Math.max(maxLength, value.length + 2);
                });
                column.width = Math.min(maxLength, 40);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            saveAs(blob, `Weekly_Report_${moment().format('YYYYMMDD')}.xlsx`);
        } catch (err) {
            console.error(err);
            setErrorMessage('Excel generation failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const periodLabel = useMemo(() => {
        if (!appliedFilters) {
            return '';
        }
        return `${moment(appliedFilters.startDate).format('DD MMM, YYYY')} - ${moment(appliedFilters.endDate).format('DD MMM, YYYY')}`;
    }, [appliedFilters]);

    const headerCellStyle = {
        padding: '8px',
        textAlign: 'center',
        fontWeight: 'bold',
        border: '1px solid #ccc',
    };
    const userCellStyle = {
        padding: '8px',
        textAlign: 'left',
        fontWeight: '500',
        border: '1px solid #ccc',
    };
    const dataCellStyle = {
        padding: '4px 8px',
        textAlign: 'center',
        border: '1px solid #ccc',
    };

    return (
        <>
            <div className="row">
                <div className="col-md-3 mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                        type="date"
                        className={`form-control rounded-pill ${startDateErr ? 'is-invalid' : ''}`}
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setStartDateErr('');
                            setErrorMessage('');
                        }}
                    />
                    {startDateErr ? <span className="error">{startDateErr}</span> : ''}
                </div>
                <div className="col-md-3 mb-3">
                    <label className="form-label">End Date</label>
                    <input
                        type="date"
                        className={`form-control rounded-pill ${endDateErr ? 'is-invalid' : ''}`}
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setEndDateErr('');
                            setErrorMessage('');
                        }}
                    />
                    {endDateErr ? <span className="error">{endDateErr}</span> : ''}
                </div>
                <div className="col-md-6 d-flex align-items-end mb-3 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-primary rounded-pill mr-2 mb-2"
                        disabled={loader}
                        onClick={handleGenerate}
                    >
                        {loader ? 'Generating...' : 'Generate Report'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary rounded-pill mr-2 mb-2"
                        onClick={handleExport}
                        disabled={exporting || !reportData || !reportData.length}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary rounded-pill mr-2 mb-2"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary rounded-pill mb-2"
                        onClick={handleUpdatePoints}
                        disabled={isUpdating}
                    >
                        {isUpdating ? 'Updating...' : 'Update Points'}
                    </button>
                </div>
            </div>
            {errorMessage ? <div className="text-danger mb-2">{errorMessage}</div> : ''}

            <div className="mt-2">
                {!appliedFilters ? (
                    <p className="text-muted mb-0">No report generated yet. Select a date range and click Generate Report.</p>
                ) : loader ? (
                    <div className="loader_lg text-center">
                        <Loader />
                    </div>
                ) : reportData && reportData.length > 0 ? (
                    <>
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="mb-1">Weekly Report Result</h5>
                                <div className="small text-muted">
                                    Period: {periodLabel} | Total Records: {reportData.length}
                                </div>
                            </div>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table" style={{ borderCollapse: 'collapse', minWidth: '700px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                                        <th rowSpan={2} style={headerCellStyle}>User Name</th>
                                        {Object.entries(groupByMonthAndDate(reportData)).map(([month, days]) => (
                                            <th
                                                key={month}
                                                colSpan={Object.keys(days).length}
                                                style={{ ...headerCellStyle, border: '2px solid #333' }}
                                            >
                                                {month}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                        {Object.entries(groupByMonthAndDate(reportData)).map(([, days]) =>
                                            Object.keys(days).map((day) => (
                                                <th key={day} style={{ ...headerCellStyle, border: '1px solid #999' }}>
                                                    {day.split(',')[0]}
                                                </th>
                                            ))
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from(new Set(reportData.map((u) => u.userName))).map((userName, idx) => (
                                        <tr key={userName} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f7f7f7' }}>
                                            <td style={userCellStyle}>{userName}</td>
                                            {Object.entries(groupByMonthAndDate(reportData)).map(([, days]) =>
                                                Object.keys(days).map((day) => {
                                                    const user = days[day].find((u) => u.userName === userName);
                                                    return (
                                                        <td key={day} style={dataCellStyle}>
                                                            {user ? user.remainingPoints : 0}
                                                        </td>
                                                    );
                                                })
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <h5 className="text-center py-4">No weekly points data found for the selected period</h5>
                )}
            </div>
        </>
    );
};

export default WeeklyReportPanel;
